<template>
  <UikitPageComponent
    :error="error"
    :pending="pending"
    @retry="refresh"
  >
    <div class="osk-wrapper">
      <h1 class="osk-page-title osk-page-title_center">
        {{ $t('components.cartPage.title') }}
        <span v-if="cartOrders.length">
          ({{ cartOrders.length }})
        </span>
      </h1>
    </div>

    <CartOrders v-model:unfinishedItems="unfinishedItems" />

    <HomeDownloadAppDesktop
      v-if="$device.isDesktop"
      hide-title
      class="osk-my-xxxl"
    >
      <template #qr-code>
        <img
          src="~assets/img/webToApp/qr_cart_over_wishlist.png"
          alt="qr_code"
          height="134px"
          width="134px"
        >
      </template>
    </HomeDownloadAppDesktop>

    <BusinessProductsWishlist />
    <BusinessAboutService />
    <BusinessProductsRecentlyWatched />
  </UikitPageComponent>
</template>

<script lang="ts" setup>
import { useCart } from '~/services/cart';
import { AnalyticsEvents, AnalyticsPages } from '~/types/analytics';
import { OrderDTO, OrderPositionDTORes } from '~/restAPI/data-contracts';
import { compilePageParam } from '~/services/analytics/oskellyAnalytics';

useHead({
  title: 'Корзина – Интернет-магазин Oskelly',
  bodyAttrs: {
    class: 'oskelly-2_0',
  },
  meta: [
    {
      name: 'description',
      content: 'Корзина – Интернет-магазин Oskelly',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: `{
        "@context": "http://www.schema.org",
        "@type": "LocalBusiness",
        "name": "Oskelly Group",
        "url": "https://oskelly.ru/",
        "logo": "https://static.oskelly.ru/images/icons/insta_logo.png?v=2.1.2_c0753d93593d6441d0dd4f9eaecf0d8f",
        "image": "https://static.oskelly.ru/images/icons/insta_img_1.jpg?v=2.1.2_0a83dbe2155b3a0373b2dc3eb9a5a5ba",
        "description": "Первый сервис для покупки и перепродажи брендовых вещей в России. Бутики и частные продавцы размещают у нас новую и почти новую брендовую одежду со скидками до -90%.\n\nКаждый день мы добавляем 100+ новых товаров, а наши эксперты собирают лучшие из них в специальные подборки.",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Варшавское шоссе, 9с28",
          "postOfficeBoxNumber": "info@oskelly.ru",
          "addressLocality": "Москва",
          "addressRegion": "Москва",
          "postalCode": "117105",
          "addressCountry": "Россия"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "55.737054",
          "longitude": "37.563080"
        },
        "hasMap": "https://goo.gl/maps/x94wA7cnGmzmkt4v9",
        "openingHours": "Mo, Tu, We, Th, Fr, Sa, Su -",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "8 (800) 707-53-08",
          "contactType": "shop"
        }
      }`,
    },
  ],
});

const { $addEvent } = useNuxtApp();
const { getUnfinishedOrders } = useCart();
const { cartOrders, getCart } = useCartStore();
const { isAuth } = useAccountStore();

const {
  data, pending, error, refresh,
} = useLazyAsyncData(() => Promise.all([
  getCart(),
  isAuth.value ? getUnfinishedOrders() : null,
]).then((result) => {
  addEventCartView(result[1]);

  return result;
}), {
  server: false,
});

const unfinishedItems = computed({
  get: () => data.value && data.value[1],

  set(value) {
    if (data.value) data.value[1] = value;
  },
});

function addEventCartView(unfinishedOrders?: OrderDTO[] | null) {
  if (process.server) return;

  const products = cartOrders.value.reduce((result, order) => {
    order.items?.forEach((item) => result.push(item));
    return result;
  }, [] as OrderPositionDTORes[]);
  const unfinishedProductIds = unfinishedOrders?.reduce<number[]>((acc, item) => {
    const items = item.items ?? [];

    acc.push(...items.map(({ id }) => id!));

    return acc;
  }, []) ?? [];

  const idItem = [
    ...unfinishedProductIds,
    ...products.map(({ id }) => id),
  ].join(',');

  $addEvent(AnalyticsEvents.CartView, {
    page: AnalyticsPages.Cart,
    chapter: 'products',
    idItem,
    products,
    previousPage: compilePageParam(window.history.state.back) ?? document.referrer,
  });
}

</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";
</style>
